import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import { workspace } from '../theme';
import { getActiveProjectId } from '../services/projectContext';
import {
  PRODUCT_TOUR_STEPS, ProductTourStep, ROUTE_REQUIRES, TOUR_COMPLETION_ROUTE, TourIntent,
} from './tourSteps';
import { scrollTargetIntoView, useTrackedRect, waitForTarget } from './useTargetRect';
import TourSpotlight from './TourSpotlight';
import TourPopover from './TourPopover';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const TOUR_VERSION = '2';
const LIFECYCLE_PREFIX = 'assertion.product-tour.v2:';
const RUN_KEY = 'assertion.product-tour.run';
const SPOTLIGHT_Z = 1298;
const POPOVER_Z = 1301;
const START_DELAY = 700;

type TourStatus = 'idle' | 'starting' | 'navigating' | 'waiting' | 'active' | 'recovering';
type Lifecycle = 'offered' | 'dismissed' | 'completed';
type TourEvent = Lifecycle | 'started' | 'skipped' | 'step_target_missing' | 'errored';

type ProductTourValue = {
  startTour: () => void;
  endTour: () => void;
  active: boolean;
  ready: boolean;
};

const ProductTourContext = createContext<ProductTourValue>({
  startTour: () => undefined, endTour: () => undefined, active: false, ready: false,
});
export const useProductTour = () => useContext(ProductTourContext);

type IntentRegistry = { register: (intent: TourIntent, handler: () => void) => () => void };
const TourIntentContext = createContext<IntentRegistry>({ register: () => () => undefined });

// Lets the shell own its own drawers and panels; the tour only expresses intent.
export function useTourIntent(intent: TourIntent, handler: () => void) {
  const { register } = useContext(TourIntentContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => register(intent, () => handlerRef.current()), [intent, register]);
}

const readStorage = (store: Storage, key: string) => {
  try { return store.getItem(key); } catch { return null; }
};
const writeStorage = (store: Storage, key: string, value: string) => {
  try { store.setItem(key, value); } catch { /* storage unavailable */ }
};
const clearStorage = (store: Storage, key: string) => {
  try { store.removeItem(key); } catch { /* storage unavailable */ }
};

const sendTourEvent = (event: TourEvent, stepCount: number) => {
  fetch(`${API_BASE}/help/tour-events`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event, tour_version: TOUR_VERSION, step_count: stepCount }),
  }).catch(() => undefined);
};

class TourErrorBoundary extends React.Component<{ onError: () => void; children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onError(); }
  render() { return this.state.failed ? null : this.props.children; }
}

export const ProductTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const [user, setUser] = useState<any>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [ready, setReady] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);

  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [status, setStatus] = useState<TourStatus>('idle');
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState<ProductTourStep[]>([]);
  const [target, setTarget] = useState<HTMLElement | null>(null);

  const originRouteRef = useRef('/dashboard');
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const activePathRef = useRef('');
  const statusRef = useRef<TourStatus>('idle');
  const runningRef = useRef(false);
  const cleanupRef = useRef<TourIntent | null>(null);
  const intentsRef = useRef(new Map<TourIntent, () => void>());
  statusRef.current = status;
  runningRef.current = running;

  const register = useCallback((intent: TourIntent, handler: () => void) => {
    intentsRef.current.set(intent, handler);
    return () => { if (intentsRef.current.get(intent) === handler) intentsRef.current.delete(intent); };
  }, []);
  const intentRegistry = useMemo(() => ({ register }), [register]);
  const dispatchIntent = useCallback((intent: TourIntent | null | undefined) => {
    if (!intent) return;
    const handler = intentsRef.current.get(intent);
    if (handler) handler();
  }, []);

  const allowed = useCallback((required?: string[]) => {
    if (!required || !required.length) return true;
    if (user?.is_platform_admin || permissions.includes('*')) return true;
    return required.some(permission => permissions.includes(permission));
  }, [permissions, user]);

  const buildSteps = useCallback(() => PRODUCT_TOUR_STEPS.filter(step => allowed(step.requires)), [allowed]);

  const resolveRoute = useCallback((step: ProductTourStep) => {
    const routes = [step.route, ...(step.routeFallbacks || [])];
    return routes.find(route => allowed(ROUTE_REQUIRES[route] ?? [])) || '/help';
  }, [allowed]);

  const lifecycleKey = user ? `${LIFECYCLE_PREFIX}${user.id || user.email || 'user'}` : '';

  useEffect(() => {
    fetch(`${API_BASE}/auth/session`).then(response => (response.ok ? response.json() : null)).then(data => {
      const sessionUser = data?.user;
      if (!sessionUser) { setReady(true); return; }
      const activeId = getActiveProjectId();
      const project = sessionUser.projects?.find((item: any) => item.id === activeId) || sessionUser.projects?.[0];
      setUser(sessionUser);
      setPermissions(project?.permissions || sessionUser.permissions || []);
      setReady(true);
      const key = `${LIFECYCLE_PREFIX}${sessionUser.id || sessionUser.email || 'user'}`;
      const resume = readStorage(window.sessionStorage, RUN_KEY);
      if (resume) {
        try {
          const parsed = JSON.parse(resume);
          if (typeof parsed?.stepIndex === 'number') {
            originRouteRef.current = parsed.originRoute || '/dashboard';
            setStepIndex(parsed.stepIndex);
            setRunning(true);
            setStatus('navigating');
            return;
          }
        } catch { clearStorage(window.sessionStorage, RUN_KEY); }
      }
      // `offered` means the user has been invited, not that they have made a
      // decision.  The shell may perform a one-time project-context reload on
      // a fresh session; retaining the prompt through that reload is important
      // for first-time users.  Only an explicit dismissal or completion should
      // suppress the invitation.
      const lifecycle = readStorage(window.localStorage, key);
      if (lifecycle !== 'dismissed' && lifecycle !== 'completed') {
        setPromptOpen(true);
        if (!lifecycle) {
          writeStorage(window.localStorage, key, 'offered');
          sendTourEvent('offered', PRODUCT_TOUR_STEPS.length);
        }
      }
    }).catch(() => setReady(true));
  }, []);

  useEffect(() => {
    if (running && !steps.length) setSteps(buildSteps());
  }, [buildSteps, running, steps.length]);

  const finish = useCallback((event: 'completed' | 'skipped' | 'errored', destination?: string) => {
    dispatchIntent(cleanupRef.current);
    cleanupRef.current = null;
    clearStorage(window.sessionStorage, RUN_KEY);
    if (lifecycleKey) writeStorage(window.localStorage, lifecycleKey, event === 'completed' ? 'completed' : 'dismissed');
    sendTourEvent(event, steps.length);
    setRunning(false);
    setPaused(false);
    setStatus('idle');
    setTarget(null);
    setSteps([]);
    setStepIndex(0);
    const route = destination || originRouteRef.current;
    if (route && route !== `${pathname}${location.search}`) navigate(route);
    window.setTimeout(() => returnFocusRef.current?.focus(), 0);
  }, [dispatchIntent, lifecycleKey, location.search, navigate, pathname, steps.length]);

  const startTour = useCallback(() => {
    const resolved = buildSteps();
    if (resolved.length < 3) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    originRouteRef.current = `${pathname}${location.search}`;
    setSteps(resolved);
    setStepIndex(0);
    setPromptOpen(false);
    setPaused(false);
    setTarget(null);
    setRunning(true);
    setStatus('starting');
    if (lifecycleKey) writeStorage(window.localStorage, lifecycleKey, 'offered');
    sendTourEvent('started', resolved.length);
    window.setTimeout(() => setStatus('navigating'), START_DELAY);
  }, [buildSteps, lifecycleKey, location.search, pathname]);

  // Advance past a step whose target could not be resolved rather than showing a detached card.
  const skipUnresolved = useCallback(() => {
    sendTourEvent('step_target_missing', steps.length);
    dispatchIntent(cleanupRef.current);
    cleanupRef.current = null;
    setStepIndex(current => {
      if (current < steps.length - 1) return current + 1;
      finish('completed');
      return current;
    });
  }, [dispatchIntent, finish, steps.length]);

  useEffect(() => {
    if (!running || paused || status === 'starting' || !steps.length) return;
    const step = steps[stepIndex];
    if (!step) return;
    const route = resolveRoute(step);
    if (pathname !== route) {
      setStatus('navigating');
      setTarget(null);
      navigate(route);
      return;
    }
    let cancelled = false;
    setStatus('waiting');
    setTarget(null);
    (async () => {
      if (step.prepare) {
        dispatchIntent(step.prepare);
        cleanupRef.current = step.cleanup || null;
        await new Promise(resolve => window.setTimeout(resolve, 60));
      }
      let element = await waitForTarget(step.target, 4000);
      if (!element && step.targetFallback) element = await waitForTarget(step.targetFallback, 1500);
      if (cancelled) return;
      if (!element) { skipUnresolved(); return; }
      await scrollTargetIntoView(element);
      if (cancelled) return;
      activePathRef.current = pathname;
      setTarget(element);
      setStatus('active');
    })();
    return () => { cancelled = true; };
  }, [running, paused, status === 'starting', stepIndex, steps, pathname]);

  // A route change the tour did not initiate pauses instead of restarting.
  useEffect(() => {
    if (runningRef.current && statusRef.current === 'active' && activePathRef.current && pathname !== activePathRef.current) {
      setPaused(true);
      setStatus('recovering');
      setTarget(null);
    }
  }, [pathname]);

  useEffect(() => {
    if (!running) return undefined;
    writeStorage(window.sessionStorage, RUN_KEY, JSON.stringify({ stepIndex, originRoute: originRouteRef.current }));
    return undefined;
  }, [running, stepIndex]);

  useEffect(() => {
    if (!running) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); finish('skipped'); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [finish, running]);

  const step = steps[stepIndex];
  const padding = step?.kind === 'region' ? 12 : 8;
  const radius = step?.kind === 'region' ? workspace.radius : 8;
  const rect = useTrackedRect(target, running && status === 'active');
  const overlayVisible = running && status !== 'starting' && status !== 'idle';

  const goNext = useCallback(() => {
    dispatchIntent(cleanupRef.current);
    cleanupRef.current = null;
    setStepIndex(current => {
      if (current < steps.length - 1) return current + 1;
      finish('completed');
      return current;
    });
  }, [dispatchIntent, finish, steps.length]);

  const goBack = useCallback(() => {
    dispatchIntent(cleanupRef.current);
    cleanupRef.current = null;
    setStepIndex(current => Math.max(0, current - 1));
  }, [dispatchIntent]);

  const value = useMemo<ProductTourValue>(() => ({
    startTour, endTour: () => finish('skipped'), active: running, ready,
  }), [finish, ready, running, startTour]);

  return (
    <ProductTourContext.Provider value={value}>
      <TourIntentContext.Provider value={intentRegistry}>
        {children}
        <TourErrorBoundary onError={() => finish('errored')}>
          {running && status === 'starting' && (
            <Box sx={{ position: 'fixed', inset: 0, zIndex: POPOVER_Z, bgcolor: 'rgba(11, 18, 32, 0.58)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Paper sx={{ px: 3, py: 2.5, borderRadius: `${workspace.radius}px`, boxShadow: workspace.raisedShadow }} role="status">
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <RocketLaunchOutlinedIcon sx={{ color: '#2450C6' }} />
                  <Typography variant="subtitle1" fontWeight={600}>{'Starting your ASSERTION product tour\u2026'}</Typography>
                </Stack>
              </Paper>
            </Box>
          )}
          {overlayVisible && step && (
            <>
              <TourSpotlight rect={rect} padding={padding} radius={radius} interactive={Boolean(step.interactive)} zIndex={SPOTLIGHT_Z} />
              {status === 'recovering' ? (
                <Box sx={{ position: 'fixed', zIndex: POPOVER_Z, bottom: 24, left: '50%', transform: 'translateX(-50%)', width: { xs: 'calc(100vw - 32px)', sm: 380 } }}>
                  <Paper sx={{ p: 2.5, borderRadius: `${workspace.radius}px`, boxShadow: workspace.raisedShadow }} role="dialog" aria-label="Product tour paused">
                    <Typography variant="subtitle1" fontWeight={700}>Tour paused</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      You moved to another area. Continue the tour from where you left off, or carry on working.
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                      <Button size="small" onClick={() => finish('skipped', `${pathname}${location.search}`)}>Exit tour</Button>
                      <Button size="small" variant="contained" onClick={() => { setPaused(false); setStatus('navigating'); }}>Continue tour</Button>
                    </Stack>
                  </Paper>
                </Box>
              ) : (
                <TourPopover
                  rect={rect}
                  padding={padding}
                  placement={step.placement}
                  title={step.title}
                  body={step.body}
                  stepNumber={stepIndex + 1}
                  total={steps.length}
                  loading={status !== 'active'}
                  isFirst={stepIndex === 0}
                  isLast={stepIndex === steps.length - 1}
                  onBack={goBack}
                  onNext={goNext}
                  onSkip={() => finish('skipped')}
                  onOpenWorkflow={() => finish('completed', TOUR_COMPLETION_ROUTE)}
                  zIndex={POPOVER_Z}
                />
              )}
              <Box aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
                {status === 'active' ? `Step ${stepIndex + 1} of ${steps.length}. ${step.title}.` : ''}
              </Box>
            </>
          )}
          {ready && promptOpen && !running && !['/', '/login', '/accept-invitation'].includes(pathname) && (
            <Paper
              role="status"
              sx={{
                position: 'fixed', zIndex: POPOVER_Z, right: { xs: 16, sm: 24 }, bottom: { xs: 16, sm: 24 },
                width: { xs: 'calc(100vw - 32px)', sm: 360 }, p: 2.25,
                borderRadius: `${workspace.radius}px`, boxShadow: workspace.raisedShadow,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Take the product tour</Typography>
                  <Typography variant="body2" color="text.secondary">A 90-second orientation across the live ASSERTION workspace.</Typography>
                </Box>
                <Button
                  size="small"
                  aria-label="Dismiss product tour prompt"
                  onClick={() => {
                    setPromptOpen(false);
                    if (lifecycleKey) writeStorage(window.localStorage, lifecycleKey, 'dismissed');
                  }}
                >
                  <CloseIcon fontSize="small" />
                </Button>
              </Stack>
              <Button variant="contained" size="small" sx={{ mt: 1.5 }} onClick={startTour}>Start tour</Button>
            </Paper>
          )}
        </TourErrorBoundary>
      </TourIntentContext.Provider>
    </ProductTourContext.Provider>
  );
};
